import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, AuthTokens } from '@/types/api';

// Base URL — defaults to '/api' so requests go through the Vite dev proxy
// (or the production reverse-proxy).  Override with VITE_API_BASE_URL only when
// you need to hit a backend directly (e.g. a remote staging server).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TOKEN_STORAGE_KEY = 'mymeals_tokens';
const LEGACY_TOKEN_STORAGE_KEY = 'auth_tokens';

// Token management
class TokenManager {
  static getTokens(): AuthTokens | null {
    try {
      const tokens = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (tokens) {
        return JSON.parse(tokens);
      }

      // Backward compatibility after API client unification.
      const legacyTokens = localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY);
      if (legacyTokens) {
        const parsedLegacyTokens = JSON.parse(legacyTokens) as AuthTokens;
        localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(parsedLegacyTokens));
        localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
        return parsedLegacyTokens;
      }

      return null;
    } catch {
      return null;
    }
  }

  static setTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  }

  static clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
  }

  static getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.access || null;
  }

  static getRefreshToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.refresh || null;
  }
}

// Create axios instance
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add token to requests
  client.interceptors.request.use(
    (config) => {
      const token = TokenManager.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle token refresh
  client.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // If unauthorized and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = TokenManager.getRefreshToken();
        if (refreshToken) {
          try {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken,
            });

            if (response.data.success && response.data.data.tokens) {
              const newTokens = response.data.data.tokens;
              TokenManager.setTokens(newTokens);
              
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
              return client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            TokenManager.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        } else {
          // No refresh token, redirect to login
          window.location.href = '/login';
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// API Client class
export class ApiClient {
  private static instance: AxiosInstance = createApiClient();

  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.instance.get(url, config);
    return response.data;
  }

  static async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.instance.post(url, data, config);
    return response.data;
  }

  static async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.instance.put(url, data, config);
    return response.data;
  }

  static async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.instance.delete(url, config);
    return response.data;
  }

  static async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.instance.patch(url, data, config);
    return response.data;
  }
}

// Export token manager for use in auth
export { TokenManager };
